import { useCallback, useEffect, useState } from 'react';
import { SlidersHorizontal } from 'lucide-react';
import Dropdown, { useDropdownMenu } from '../../Dropdown.jsx';
import TopbarGlobalSearchFilterPanel from './TopbarGlobalSearchFilterPanel.jsx';
import {
  applyCategoryToFilterIds,
  getCategoryIdFromTypeFilter,
  selectSingleFilterInGroup,
  GLOBAL_SEARCH_ACTIVE_CATEGORY_CLASS,
  GLOBAL_SEARCH_FILTERS_SCROLL_CLASS,
  GLOBAL_SEARCH_INACTIVE_CATEGORY_CLASS,
  GLOBAL_SEARCH_SURFACE_CLASS,
} from './constants.js';

const EMPTY_ARRAY = [];
const EMPTY_LABELS = {};

function FilterPanelContent({ draftIds, onToggle, onClearAll, onApply, sections, labels }) {
  const menu = useDropdownMenu();

  const handleDone = useCallback(() => {
    onApply?.(draftIds);
    menu?.close?.();
  }, [draftIds, menu, onApply]);

  return (
    <TopbarGlobalSearchFilterPanel
      selectedIds={draftIds}
      onToggle={onToggle}
      sections={sections}
      onClearAll={onClearAll}
      onDone={handleDone}
      labels={labels}
    />
  );
}

/**
 * @param {object} props
 * @param {Array<{ id: string, label: string, icon?: import('lucide-react').LucideIcon }>} props.categories
 * @param {Array<{ id: string, title: string, options: Array<{ id: string, label: string }> }>} props.filterSections
 * @param {string} props.activeCategory
 * @param {(categoryId: string) => void} [props.onCategoryChange]
 * @param {string[]} [props.selectedFilterIds]
 * @param {(ids: string[]) => void} [props.onFiltersChange]
 * @param {Record<string, string>} [props.labels]
 * @param {string} [props.filtersAriaLabel]
 */
const TopbarGlobalSearchFilters = ({
  categories = EMPTY_ARRAY,
  filterSections = EMPTY_ARRAY,
  activeCategory = 'all',
  onCategoryChange,
  selectedFilterIds = EMPTY_ARRAY,
  onFiltersChange,
  labels = EMPTY_LABELS,
  filtersAriaLabel = 'Search filters',
}) => {
  const [draftIds, setDraftIds] = useState(selectedFilterIds);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const categoryForTabs = isPanelOpen
    ? getCategoryIdFromTypeFilter(draftIds)
    : activeCategory;

  useEffect(() => {
    if (!isPanelOpen) setDraftIds(selectedFilterIds);
  }, [selectedFilterIds, isPanelOpen]);

  const selectDraftFilter = useCallback(
    (id) => {
      const next = selectSingleFilterInGroup(draftIds, id);
      setDraftIds(next);
      onFiltersChange?.(next);
      if (id.startsWith('type-')) {
        const categoryId = id.slice('type-'.length);
        onCategoryChange?.(next.includes(id) ? categoryId : 'all');
      }
    },
    [draftIds, onCategoryChange, onFiltersChange],
  );

  const handleClearAllDraft = useCallback(() => {
    setDraftIds([]);
    onFiltersChange?.([]);
    onCategoryChange?.('all');
  }, [onCategoryChange, onFiltersChange]);

  const handleApplyFilters = useCallback(
    (ids) => {
      onFiltersChange?.(ids);
    },
    [onFiltersChange],
  );

  const handleCategoryClick = useCallback(
    (categoryId) => {
      onCategoryChange?.(categoryId);
      if (isPanelOpen) {
        setDraftIds((current) => applyCategoryToFilterIds(current, categoryId));
      }
    },
    [isPanelOpen, onCategoryChange],
  );

  const filterButton = (
    <button
      type="button"
      aria-expanded={isPanelOpen}
      aria-haspopup="dialog"
      className={[
        'inline-flex h-7 max-h-7 w-7 shrink-0 items-center justify-center rounded-lg border text-content transition-colors',
        'max-md:h-11 max-md:w-11',
        isPanelOpen
          ? 'border-violet-500/60 bg-violet-600 text-white'
          : 'border-[#5C5C5C] bg-white hover:bg-black/[0.04] dark:border-white/10 dark:bg-[#1a2030] dark:text-gray-400 dark:hover:bg-white/[0.06] dark:hover:text-gray-200',
      ].join(' ')}
      aria-label={labels.filtersTooltipTitle ?? labels.advancedFilters ?? 'Filters'}
    >
      <SlidersHorizontal className="h-3.5 w-3.5" strokeWidth={2} />
    </button>
  );

  return (
    <div
      className={[
        'flex shrink-0 items-center gap-2 px-4 py-2.5',
        'max-md:px-3 max-md:py-2',
        GLOBAL_SEARCH_SURFACE_CLASS,
      ].join(' ')}
      role="toolbar"
      aria-label={filtersAriaLabel}
    >
      <Dropdown
        usePortal
        align="left"
        className="inline-flex shrink-0"
        menuMaxHeight="none"
        menuClassName="!min-w-0 !w-auto !border-0 !bg-transparent !p-0 !shadow-none"
        onOpenChange={setIsPanelOpen}
        trigger={filterButton}
      >
        <FilterPanelContent
          draftIds={draftIds}
          onToggle={selectDraftFilter}
          onClearAll={handleClearAllDraft}
          onApply={handleApplyFilters}
          sections={filterSections}
          labels={labels}
        />
      </Dropdown>

      <div
        className={[
          'scrollbar-aimsos-auto-sm flex min-w-0 flex-1 items-center gap-2 overflow-x-auto overflow-y-hidden',
          GLOBAL_SEARCH_FILTERS_SCROLL_CLASS,
        ].join(' ')}
      >
        {categories.map(({ id, label, icon: Icon, count }) => {
          const isActive = categoryForTabs === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => handleCategoryClick(id)}
              className={[
                'inline-flex h-7 shrink-0 snap-start items-center gap-1 rounded-full px-2.5 text-xs font-medium leading-5 transition-colors',
                'max-md:h-9 max-md:px-3',
                isActive ? GLOBAL_SEARCH_ACTIVE_CATEGORY_CLASS : GLOBAL_SEARCH_INACTIVE_CATEGORY_CLASS,
              ].join(' ')}
              aria-pressed={isActive}
            >
              <Icon className="h-3.5 w-3.5 shrink-0" strokeWidth={2} aria-hidden />
              {label}
              {typeof count === 'number' ? (
                <span className="tabular-nums opacity-70">{count > 99 ? '99+' : count}</span>
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default TopbarGlobalSearchFilters;
